import React, { useEffect, useState } from "react";
import { ScrollText, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";
import { Input } from "@/components/ui/input";

const AdminAuditLog = () => {
  const [entries, setEntries] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [actionFilter, setActionFilter] = useState("");

  const reload = () => {
    const params = new URLSearchParams({ limit: "100" });
    if (actionFilter) params.set("action", actionFilter);
    api.get(`/admin/audit-log?${params.toString()}`).then((r) => setEntries(r.data)).catch(() => setEntries([]));
  };
  useEffect(() => { reload(); }, [actionFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <AdminPage
      title="Audit log"
      subtitle="Every admin mutation is recorded — this is the source of truth for platform changes."
      testId="admin-audit-log-page"
      action={<Input placeholder="Filter by action prefix (e.g. dealer.)" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="w-64" data-testid="admin-audit-filter" />}
    >
      {!entries ? <div className="h-64 bg-slate-100 rounded-2xl animate-pulse" /> :
        entries.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center">
            <ScrollText size={28} className="mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500 text-sm">No audit entries yet.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admin-audit-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="w-8 px-2 py-3"></th>
                  <th className="text-left px-4 py-3 font-semibold">Timestamp</th>
                  <th className="text-left px-4 py-3 font-semibold">Admin</th>
                  <th className="text-left px-4 py-3 font-semibold">Action</th>
                  <th className="text-left px-4 py-3 font-semibold">Target</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const isOpen = expanded.has(e.id);
                  return (
                    <React.Fragment key={e.id}>
                      <tr className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => toggle(e.id)} data-testid={`admin-audit-row-${e.id}`}>
                        <td className="px-2 py-3 text-slate-400">{isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</td>
                        <td className="px-4 py-3 text-xs text-slate-600">{new Date(e.created_at).toLocaleString()}</td>
                        <td className="px-4 py-3 text-slate-700">{e.admin_user_email}</td>
                        <td className="px-4 py-3"><span className="inline-flex rounded-full border border-brand/30 bg-brand-light px-2 py-0.5 text-[11px] font-semibold text-brand">{e.action}</span></td>
                        <td className="px-4 py-3 text-slate-700">{e.target_label || e.target_type} <span className="text-slate-400 text-[11px]">({e.target_type})</span></td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50 border-t border-slate-100">
                          <td colSpan={5} className="px-6 py-3">
                            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Details</p>
                            <pre className="text-[11px] text-slate-800 bg-white border border-slate-200 rounded p-3 overflow-auto" data-testid={`admin-audit-details-${e.id}`}>{JSON.stringify(e.details, null, 2)}</pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </AdminPage>
  );
};

export default AdminAuditLog;
